/* eslint-disable max-lines */
/* eslint-disable max-statements */
import React, {
  ReactElement,
  useEffect,
  useMemo,
  useCallback,
  useState
} from 'react';
import _ from 'lodash';
import * as baselinesService from 'services/baselines.service';
import * as workcentersService from 'services/workcenters.service';
import { LoadingOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import {
  ApiStateRecord,
  callApiAndUpdateState,
  useApiStateRecord
} from '@exxonmobil/uit-uda-ui-components-lib';
import { BaselinesData, MODE, WorkCenterBaseline } from 'models/baselines';
import { WorkCenter } from 'models/workcenters';
import { addWorkCentersWithoutBaseline } from 'pages/Baselines/Baselines.helpers';
import { rejectSelected as rejectSelectedFn } from 'domain/rejectSelected';
import { approveSelected as approveSelectedFn } from 'domain/approveSelected';
import styles from './Provider.module.scss';
import {
  fetchingBaselinesMessage,
  BaselinesProviderValueType,
  initStateDataValue,
  defaultFullBaselinesState,
  defaultBaselinesState,
  defaultWorkCentersState
} from './BaselinesProvider.helpers';

import { isApprover } from './Provider.helpers';

const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

/* ******************* The provider component ******************* */

const context =
  React.createContext<BaselinesProviderValueType>(initStateDataValue);

const renderProviderComponent = (
  children: ReactElement,
  getBaselines: () => Promise<any>,
  getWorkCenters: () => Promise<any>,
  updateBaselineSelection: Function,
  updateBaselineSelectionAll: Function,
  updateMode: Function,
  cloneApprovedToRequested: Function,
  stateRecord: ApiStateRecord,
  workCenters: WorkCenter[],
  isUserApprover: boolean,
  rejectSelected: (
    workCenterState: ApiStateRecord<WorkCenterBaseline[]>
  ) => Promise<PromiseSettledResult<Response>[]>,
  approveSelected: (
    workCenterState: ApiStateRecord<WorkCenterBaseline[]>
  ) => Promise<PromiseSettledResult<Response>[]>,
  loadingMessage: string
) => {
  const { error, responseStatus } = stateRecord.state;

  const contextValue = useMemo(
    () => ({
      stateRecord,
      getBaselines,
      getWorkCenters,
      updateBaselineSelection,
      updateBaselineSelectionAll,
      updateMode,
      cloneApprovedToRequested,
      workCenters,
      isUserApprover,
      rejectSelected: () => rejectSelected(stateRecord),
      approveSelected: () => approveSelected(stateRecord)
    }),
    [stateRecord.state, stateRecord.state.data]
  );

  if (!_.isEmpty(error)) {
    throw Error('Baselines data is not available');
  }

  if (responseStatus === 0) {
    return (
      <div className={styles.wrapper}>
        <Spin tip={loadingMessage} indicator={antIcon} size="large" />
      </div>
    );
  }

  return <context.Provider value={contextValue}>{children}</context.Provider>;
};

type BaselinesProviderCompProps = {
  children: ReactElement;
};
const BaselinesProviderComp = ({
  children
}: BaselinesProviderCompProps): ReactElement => {
  const [loadingMessage, setLoadingMessage] = useState(
    fetchingBaselinesMessage
  );
  const baselinesStateRecord = useApiStateRecord<BaselinesData[]>(
    defaultBaselinesState.data
  );
  const workCentersStateRecord = useApiStateRecord<WorkCenter[]>(
    defaultWorkCentersState.data
  );
  const fullBaselinesStateRecord = useApiStateRecord<WorkCenterBaseline[]>(
    defaultFullBaselinesState.data
  );

  const getBaselines = useCallback(async () => {
    setLoadingMessage(fetchingBaselinesMessage);
    const response = await callApiAndUpdateState({
      stateRecord: baselinesStateRecord,
      apiServiceFunction: baselinesService.getBaselines
    });
    return response;
  }, []);

  const updateMode = (mode: MODE, workCenter: string) => {
    fullBaselinesStateRecord.state.data.forEach(
      (baseline: WorkCenterBaseline) => {
        if (baseline.workCenter === workCenter) {
          _.set(baseline, ['mode'], mode);
        }
      }
    );

    fullBaselinesStateRecord.setState({ ...fullBaselinesStateRecord.state });
  };

  const cloneApprovedToRequested = (mode: MODE, workCenter: string) => {
    fullBaselinesStateRecord.state.data.forEach(
      (baseline: WorkCenterBaseline) => {
        if (
          baseline.workCenter === workCenter &&
          mode === 'edit' &&
          (!baseline.requested || baseline.requested.length === 0)
        ) {
          _.set(baseline, ['requested'], baseline.approved);
        }
      }
    );

    fullBaselinesStateRecord.setState({ ...fullBaselinesStateRecord.state });
  };

  const updateBaselineSelection = (isSelected: boolean, workCenter: string) => {
    fullBaselinesStateRecord.state.data.forEach(
      (baseline: WorkCenterBaseline) => {
        if (baseline.workCenter === workCenter)
          _.set(baseline, ['isSelected'], isSelected);
      }
    );

    fullBaselinesStateRecord.setState({ ...fullBaselinesStateRecord.state });
  };
  const updateBaselineSelectionAll = (isSelected: boolean) => {
    fullBaselinesStateRecord.state.data
      .filter((baseline) => baseline.requested && baseline.requested.length)
      .forEach((baseline: WorkCenterBaseline) => {
        _.set(baseline, ['isSelected'], isSelected);
      });
  };

  const rejectSelected = (currentState: ApiStateRecord<WorkCenterBaseline[]>) =>
    rejectSelectedFn(currentState);

  const approveSelected = (
    currentState: ApiStateRecord<WorkCenterBaseline[]>
  ) => approveSelectedFn(currentState);

  const getWorkCenters = useCallback(async () => {
    return callApiAndUpdateState({
      stateRecord: workCentersStateRecord,
      apiServiceFunction: workcentersService.getWorkCenters
    });
  }, []);

  useEffect(() => {
    getBaselines();
    getWorkCenters();
  }, []);

  useEffect(() => {
    const { state: baselinesState } = baselinesStateRecord;
    const { state: workCentersState } = workCentersStateRecord;
    const { loading, error, responseStatus } = baselinesStateRecord.state;

    if (baselinesState.data.length && workCentersState.data.length) {
      fullBaselinesStateRecord.setState({
        data: addWorkCentersWithoutBaseline(
          workCentersState.data,
          baselinesState.data
        ),
        loading,
        error,
        responseStatus
      });
    }
  }, [baselinesStateRecord.state.data, workCentersStateRecord.state.data]);

  const workCenters = workCentersStateRecord.state.data;
  const isUserApprover = isApprover();

  return renderProviderComponent(
    children,
    getBaselines,
    getWorkCenters,
    updateBaselineSelection,
    updateBaselineSelectionAll,
    updateMode,
    cloneApprovedToRequested,
    fullBaselinesStateRecord,
    workCenters,
    isUserApprover,
    rejectSelected,
    approveSelected,
    loadingMessage
  );
};

export const BaselinesProvider = BaselinesProviderComp;

export const BaselinesContext = context;
